import React from "react";
import { Link } from "react-router-dom";
import { FaBrain, FaProjectDiagram, FaShapes, FaAtom, FaBookOpen, FaChartLine, FaLightbulb, FaArrowRight, FaRocket, FaGraduationCap, FaPuzzlePiece, FaStar, FaAward, FaUsers, FaTrophy, FaClock } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CircularText from './CircularText';
import Logos from './S_LOGO-.png';
import Lanyard from './components/Lanyard';
import DarkVeil from './components/DarkVeil';


const LandingPage = () => {
  return (
    <div className="font-space bg-white text-black min-h-screen relative">
      {/* DarkVeil Background */}
      <div style={{ width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none' }}>
        <DarkVeil />
      </div>

      <div className="relative z-10">
        <Navbar />

        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 lg:py-40 flex flex-col md:flex-row items-center">
            {/* Text Content */}
            <div className="md:w-1/2 mb-8 sm:mb-12 md:mb-0 text-center md:text-left w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 md:mb-8 leading-tight text-white px-2 sm:px-0">
                Elevate Your
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-300 to-teal-500 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                  Mathematical Journey
                </span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 md:mb-10 max-w-lg mx-auto md:mx-0 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)] px-2 sm:px-0">
                Discover a revolutionary approach to mathematics learning led by a seasoned IGCSE educator. Our methodology centers on fundamental concepts, fostering profound comprehension and enhancing analytical reasoning abilities.
              </p>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center md:justify-start px-2 sm:px-0">
                <Link
                  to="/signup"
                  className="px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-teal-600 to-sky-600 hover:from-teal-500 hover:to-sky-500 text-white font-semibold text-sm sm:text-base transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-teal-500/40 hover:scale-105 text-center group"
                >
                  Get Started <FaArrowRight className="inline-block ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>


            {/* Visual Element */}
            <div className="md:w-1/2 flex justify-center relative mt-4 sm:mt-8 md:mt-0 w-full">
              <div className="relative w-full max-w-lg px-2 sm:px-4 md:px-0">
                {/* Animated background orbs */}
                <div className="absolute -top-10 sm:-top-20 -left-10 sm:-left-20 w-24 h-24 sm:w-40 sm:h-40 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-10 sm:-bottom-20 -right-10 sm:-right-20 w-24 h-24 sm:w-40 sm:h-40 bg-gradient-to-r from-sky-500 to-teal-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>

                {/* Main visual container - Modern card stack design */}
                <div className="relative">
                  {/* Central featured card */}
                  <div className="relative z-10 bg-white/10 border border-white/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 backdrop-blur-sm">
                        <FaGraduationCap className="text-2xl sm:text-3xl md:text-4xl text-white" />
                      </div>
                      <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-1 sm:mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">Expert Guidance</h3>
                      <p className="text-white/90 text-xs sm:text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">IGCSE Mathematics Excellence</p>
                    </div>
                  </div>

                  {/* Floating feature cards around the main card - Hidden on mobile, shown on larger screens */}
                  <div className="hidden sm:block absolute -top-2 sm:-top-4 -right-2 sm:-right-4 w-20 h-20 sm:w-24 md:w-28 sm:h-24 md:h-28 bg-white rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-4 shadow-xl border-2 border-gray-100 hover:border-teal-300 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-teal-500/20 group z-20">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300 text-white">
                      <FaRocket className="text-sm sm:text-lg md:text-xl" />
                    </div>
                    <h4 className="font-semibold text-[10px] sm:text-xs text-gray-900 text-center drop-shadow-[0_1px_2px_rgba(255,255,255,0.8)]">Accelerated</h4>
                  </div>

                  <div className="hidden sm:block absolute -bottom-2 sm:-bottom-4 -left-2 sm:-left-4 w-20 h-20 sm:w-24 md:w-28 sm:h-24 md:h-28 bg-white rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-4 shadow-xl border-2 border-gray-100 hover:border-teal-300 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-teal-500/20 group z-20">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-r from-cyan-500 to-sky-500 flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300 text-white">
                      <FaPuzzlePiece className="text-sm sm:text-lg md:text-xl" />
                    </div>
                    <h4 className="font-semibold text-[10px] sm:text-xs text-gray-800 text-center">Concepts</h4>
                  </div>

                  <div className="hidden md:block absolute top-1/2 -right-6 md:-right-8 lg:-right-12 w-20 h-20 md:w-24 lg:w-28 md:h-24 lg:h-28 bg-white rounded-xl md:rounded-2xl p-2 md:p-3 lg:p-4 shadow-xl border-2 border-gray-100 hover:border-teal-300 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-teal-500/20 group z-20 transform -translate-y-1/2">
                    <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-lg md:rounded-xl bg-gradient-to-r from-sky-500 to-teal-500 flex items-center justify-center mb-1 md:mb-2 group-hover:scale-110 transition-transform duration-300 text-white">
                      <FaStar className="text-sm md:text-lg lg:text-xl" />
                    </div>
                    <h4 className="font-semibold text-[10px] md:text-xs text-gray-800 text-center">Excellence</h4>
                  </div>

                  <div className="hidden md:block absolute top-1/2 -left-6 md:-left-8 lg:-left-12 w-20 h-20 md:w-24 lg:w-28 md:h-24 lg:h-28 bg-white rounded-xl md:rounded-2xl p-2 md:p-3 lg:p-4 shadow-xl border-2 border-gray-100 hover:border-teal-300 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-teal-500/20 group z-20 transform -translate-y-1/2">
                    <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-lg md:rounded-xl bg-gradient-to-r from-teal-400 to-cyan-400 flex items-center justify-center mb-1 md:mb-2 group-hover:scale-110 transition-transform duration-300 text-white">
                      <FaBrain className="text-sm md:text-lg lg:text-xl" />
                    </div>
                    <h4 className="font-semibold text-[10px] md:text-xs text-gray-800 text-center">Thinking</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Features Section */}
        <section className="py-12 sm:py-16 md:py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center">

            {/* LEFT SIDE — TEXT */}
            <div className="flex flex-col md:w-1/2 text-center md:text-left mb-8 sm:mb-12 md:mb-0 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] px-2 sm:px-0">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-300 to-teal-500">Mastery</span>{' '}
                <span className="text-white">in IGCSE Maths</span>
              </h2>

              <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-3xl mx-auto md:mx-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)] px-2 sm:px-0">
                Guided by an experienced IGCSE Maths teacher, our approach focuses on
                deep conceptual understanding and long-term student success.
              </p>
            </div>

            {/* RIGHT SIDE — CIRCULAR TEXT */}
            <div className="md:w-1/2 flex justify-center max-w-full overflow-hidden md:px-0 px-4 sm:px-10 py-6 sm:py-10 w-full">
              <div className="scale-75 sm:scale-100 md:scale-125 lg:scale-150 origin-center">
                <div className="flex bg-[url('/Xmaths.png')] bg-center bg-[length:120px] bg-no-repeat filter brightness-0 invert">
                  <CircularText
                    text="✦ SHEMIES ✦ MATH ✦ IGCSE "
                    onHover="speedUp"
                    spinDuration={20}
                  />
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Experience & Achievements Section */}
        <section className="py-12 sm:py-16 md:py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Section Header */}
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-300 to-teal-500">Proven Excellence</span>
                <span className="text-white"> Over Years</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)] px-2 sm:px-0">
                Years of dedicated teaching experience, transforming students' mathematical understanding and achieving outstanding results.
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {/* Years of Experience */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-teal-500/20">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
                    <FaClock className="text-3xl sm:text-4xl text-white" />
                  </div>
                  <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    9+
                  </h3>
                  <p className="text-white/90 text-sm sm:text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    Years of Experience
                  </p>
                </div>
              </div>

              {/* Students Taught */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-teal-500/20">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
                    <FaUsers className="text-3xl sm:text-4xl text-white" />
                  </div>
                  <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    500+
                  </h3>
                  <p className="text-white/90 text-sm sm:text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    Students Taught
                  </p>
                </div>
              </div>

              {/* IGCSE Expertise */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-teal-500/20">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
                    <FaBookOpen className="text-3xl sm:text-4xl text-white" />
                  </div>
                  <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    IGCSE
                  </h3>
                  <p className="text-white/90 text-sm sm:text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    Specialized Expertise
                  </p>
                </div>
              </div>

              {/* Proven Results */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-teal-500/20">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
                    <FaTrophy className="text-3xl sm:text-4xl text-white" />
                  </div>
                  <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    Proven
                  </h3>
                  <p className="text-white/90 text-sm sm:text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                    Track Record
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mt-6 sm:mt-8 md:mt-12">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                    <FaChartLine className="text-2xl sm:text-3xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                      Continuous Improvement
                    </h3>
                    <p className="text-white/90 text-sm sm:text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                      Constantly evolving teaching methods based on student feedback and educational research to ensure the best learning outcomes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                    <FaLightbulb className="text-2xl sm:text-3xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                      Innovative Methods
                    </h3>
                    <p className="text-white/90 text-sm sm:text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                      Utilizing modern teaching techniques and personalized approaches to help each student reach their full potential in mathematics.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lanyard 3D Section */}
        <section className="relative w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-screen pb-20 sm:pb-12 md:pb-8 lg:pb-0 mb-8 sm:mb-4 md:mb-0 overflow-hidden touch-none select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}>
          <div className="w-full h-full touch-pan-y touch-pan-x" style={{ touchAction: 'pan-y pan-x' }}>
            <Lanyard cameraDistance={20} />
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default LandingPage;